import { ClientLoggingService } from '../shared/client-logging.service';
import { Injectable, inject } from '@angular/core';
import { lastValueFrom } from 'rxjs';
import { FirebaseAuthService } from './firebase-auth.service';
import { EnvironmentService } from './environment.service';
import { AuthInterceptor } from './auth.interceptor';
import { ProgressService } from './progress.service';
import { BULK_INGESTION_DISPATCH_CONCURRENCY, MAX_FILE_UPLOAD_SIZE_BYTES, MAX_FILE_UPLOAD_SIZE_MB } from './constants';
import { runWithConcurrencyLimit } from './concurrency.util';
import {
  ArchitecturalPlanServiceClient,
  ArchitecturalPlanWriteServiceClient,
} from '../../generated.commonjs/ApiServiceClientPb';
import {
  GetProjectMetadataRequest,
  ListArchitecturalProjectInputFilesRequest,
  CreateArchitecturalPlanRequest,
  GenerateUploadUrlRequest,
  NotifyUploadCompleteRequest,
  IngestFileIntoProjectRequest,
} from '../../generated.commonjs/api_pb';
import { createBillingOperationId, TaskService } from './task.service';
import { type BalanceCheckResult, BillingService } from './billing.service';
import { isOperationWideBillingFailure } from './billing-error-utils';

const BILLING_VALIDATION_UNAVAILABLE_MESSAGE =
  'Billing validation is unavailable. No ingestion or paid work was started. Please try again; if this continues, contact support.';

export interface ProjectCreationResult {
  success: boolean;
  projectId: string;
  error?: string;
  message?: string;
  taskIds?: string[];
  pageCount?: number;
  errorField?: 'projectId';
  canonicalFilename?: string;
  replacedExistingFile?: boolean;
  uploadVerified?: boolean;
  metadataGenerated?: boolean;
  partialUpload?: boolean;
  fileId?: string;
}

export interface ProjectMetadata {
  project_id: string;
  project_name: string;
  description: string;
  created_by: string;
  created_at: string;
  status: string;
  address?: {
    street: string;
    city: string;
    state: string;
    postalCode: string;
    country: string;
    googlePlaceId?: string;
    latitude?: number;
    longitude?: number;
    formattedAddress?: string;
  };
  modelConfig?: {
    useCaseOverrides: Array<{
      useCase: string;
      profileId: string;
      roleProfiles: Record<string, string>;
    }>;
  };
  ingestionSettings?: {
    enableVisualVerification?: boolean;
    enableRegionHierarchy?: boolean;
  };
  power_user_mode?: boolean;
}

export interface UploadedFile {
  name: string;
  size: number;
  uploadedAt: string;
  path: string;
  pageCount?: number;
  fileType?: string;
  lastModified?: string;
}

/** Orchestrates new-project creation: calls the backend, uploads plan PDFs, and tracks async ingestion tasks. */
@Injectable({
  providedIn: 'root',
})
export class ProjectCreationService {
  private firebaseAuthService = inject(FirebaseAuthService);
  private environmentService = inject(EnvironmentService);
  private taskService = inject(TaskService);
  private progressService = inject(ProgressService);
  private billingService = inject(BillingService);

  private client: ArchitecturalPlanServiceClient;
  private writeClient: ArchitecturalPlanWriteServiceClient;

  constructor() {
    const apiServer = this.environmentService.getApiServer();
    const authInterceptor = new AuthInterceptor(this.firebaseAuthService);
    const options = {
      unaryInterceptors: [authInterceptor],
    };

    this.client = new ArchitecturalPlanServiceClient(`https://${apiServer}:443`, null, options);
    this.writeClient = new ArchitecturalPlanWriteServiceClient(`https://${apiServer}:443`, null, options);
  }

  /**
   * Complete project creation workflow:
   * 1. Create project
   * 2. Upload file (if provided)
   * 3. Ingest file into project (if file provided and pages !== 'none')
   */
  async createProject(
    projectId: string,
    projectName: string,
    file: File | null,
    pages: 'all' | 'custom' | 'none',
    customPages?: string
  ): Promise<ProjectCreationResult> {
    try {
      ClientLoggingService.log('INFO', `[ProjectCreationService] Starting project creation for: ${projectId}`);

      if (!file && pages !== 'none') {
        return {
          success: false,
          projectId,
          error: `A file is required when pages is '${pages}'. Use pages='none' to create a bare project.`,
        };
      }

      const fileName = file?.name ?? '';

      if (file) {
        ClientLoggingService.log(
          'INFO',
          `[ProjectCreationService] File details: ${file.name}, size: ${file.size} bytes, type: ${file.type}`
        );

        if (file.size > MAX_FILE_UPLOAD_SIZE_BYTES) {
          return {
            success: false,
            projectId,
            error: `File size ${(file.size / 1024 / 1024).toFixed(2)}MB exceeds the ${MAX_FILE_UPLOAD_SIZE_MB}MB limit`,
          };
        }
      } else {
        ClientLoggingService.log('INFO', `[ProjectCreationService] Creating bare project (no file)`);
      }

      // Step 1: Create the project. A duplicate ID is a user-correctable field error.
      ClientLoggingService.log('INFO', `[ProjectCreationService] Step 1: Creating project ${projectId}`);
      const createResult = await this.createArchitecturalPlan(projectId, projectName, fileName);
      if (!createResult.success) {
        ClientLoggingService.log('ERROR', `[ProjectCreationService] Step 1 failed:`, createResult.error);
        return createResult;
      }
      ClientLoggingService.log('INFO', `[ProjectCreationService] Step 1 completed successfully`);

      // Bare project: skip upload and ingestion
      if (!file) {
        ClientLoggingService.log('INFO', `[ProjectCreationService] Bare project created — skipping upload/ingestion`);
        return {
          success: true,
          projectId,
          taskIds: [],
          message: 'Project created successfully (no file uploaded).',
        };
      }

      // Step 2: Upload the file (with recovery logic)
      ClientLoggingService.log('INFO', `[ProjectCreationService] Step 2: Uploading file ${file.name}`);
      const uploadResult = await this.uploadFileToProject(projectId, file);
      if (!uploadResult.success) {
        if (uploadResult.error && uploadResult.error.includes('already exists')) {
          ClientLoggingService.log(
            'WARN',
            `[ProjectCreationService] File ${file.name} already exists in project ${projectId} - continuing with ingestion...`
          );
        } else {
          ClientLoggingService.log('ERROR', `[ProjectCreationService] Step 2 failed:`, uploadResult.error);
          ClientLoggingService.log(
            'WARN',
            `[ProjectCreationService] Upload failed, but project exists - attempting to continue with ingestion...`
          );
        }
      } else {
        ClientLoggingService.log('INFO', `[ProjectCreationService] Step 2 completed successfully`);
      }

      if (!uploadResult.success && uploadResult.partialUpload) {
        return {
          success: true,
          projectId,
          taskIds: [],
          canonicalFilename: uploadResult.canonicalFilename,
          replacedExistingFile: uploadResult.replacedExistingFile,
          uploadVerified: true,
          metadataGenerated: false,
          partialUpload: true,
          fileId: uploadResult.fileId,
          error: uploadResult.error,
          message: `Project created, but ${uploadResult.error || 'the uploaded file still needs metadata registration.'}`,
        };
      }

      if (!uploadResult.success) {
        return {
          success: true,
          projectId,
          taskIds: [],
          uploadVerified: false,
          error: uploadResult.error,
          message: `Project created successfully, but file upload failed: ${uploadResult.error || 'Unknown error'}. You can try uploading the file again from the project page.`,
        };
      }

      // Step 3: Start async ingestion (with Cloud Run Jobs) - skip if pages is 'none'
      if (pages === 'none') {
        ClientLoggingService.log('INFO', `[ProjectCreationService] Pages set to 'none' - skipping ingestion`);

        let message = 'Project created and file uploaded successfully. Ingestion can be started later.';
        if (uploadResult.replacedExistingFile) {
          message = 'Project created successfully. The existing file was updated in place and is ready for ingestion.';
        }

        return {
          success: true,
          projectId,
          taskIds: [],
          pageCount: uploadResult.pageCount,
          canonicalFilename: uploadResult.canonicalFilename,
          replacedExistingFile: uploadResult.replacedExistingFile,
          uploadVerified: uploadResult.uploadVerified,
          metadataGenerated: uploadResult.metadataGenerated,
          fileId: uploadResult.fileId,
          message: message,
        };
      }

      // Step 3: Start async ingestion (with Cloud Run Jobs)
      ClientLoggingService.log(
        'INFO',
        `[ProjectCreationService] Step 3: Starting async ingestion for file ${file.name}`
      );
      const ingestResult = await this.ingestFileIntoProjectAsync(
        projectId,
        uploadResult.canonicalFilename || file.name,
        pages === 'all' ? uploadResult.pageCount : undefined,
        pages === 'custom' ? customPages : undefined
      );

      if (!ingestResult.success) {
        ClientLoggingService.log('ERROR', `[ProjectCreationService] Step 3 failed:`, ingestResult.error);
        ClientLoggingService.log(
          'WARN',
          `[ProjectCreationService] Project ${projectId} was created but ingestion failed - user can retry ingestion later`
        );

        return {
          success: true,
          projectId,
          taskIds: [],
          message: `Project created successfully, but file ingestion failed: ${ingestResult.error}. You can try uploading the file again from the project page.`,
        };
      }

      ClientLoggingService.log(
        'INFO',
        `[ProjectCreationService] Step 3 completed successfully - ${ingestResult.taskIds?.length || 0} async tasks created`
      );

      ClientLoggingService.log(
        'INFO',
        `[ProjectCreationService] All steps completed successfully for project ${projectId}`
      );
      return {
        success: true,
        projectId,
        taskIds: ingestResult.taskIds,
        message: ingestResult.message || 'Project created and async processing started successfully',
      };
    } catch (error) {
      ClientLoggingService.log('ERROR', '[ProjectCreationService] Error in project creation workflow:', error);
      return {
        success: false,
        projectId,
        error: error instanceof Error ? error.message : 'Unknown error occurred',
      };
    }
  }

  /**
   * Step 1: Create the architectural plan project
   */
  private async createArchitecturalPlan(
    projectId: string,
    projectName: string,
    fileName: string
  ): Promise<ProjectCreationResult> {
    try {
      // Create the request
      const request = new CreateArchitecturalPlanRequest();
      request.setProjectId(projectId);
      request.setProjectName(projectName);
      request.setDescription(fileName ? `Project created from uploaded file: ${fileName}` : projectName);

      ClientLoggingService.log('INFO', `[ProjectCreationService] Creating architectural plan: ${projectId}`);
      console.time(`CreateProject-${projectId}`); // Track timing for race condition detection

      // Make the gRPC call
      const response = await this.writeClient.createArchitecturalPlan(request, null);
      console.timeEnd(`CreateProject-${projectId}`);

      ClientLoggingService.log('INFO', `[ProjectCreationService] Project created successfully:`, response.toObject());

      return {
        success: true,
        projectId,
        message: 'Project created successfully',
      };
    } catch (error) {
      ClientLoggingService.log('ERROR', '[ProjectCreationService] Error creating architectural plan:', error);

      // Enhanced error handling for race condition detection
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';

      // Check if this is an "already exists" error that might be from a race condition
      if (errorMessage.includes('already exists')) {
        ClientLoggingService.log(
          'WARN',
          '[ProjectCreationService] Detected "already exists" error - possible race condition'
        );
        return {
          success: false,
          projectId,
          error: 'A project with this ID already exists.',
          errorField: 'projectId',
        };
      }

      return {
        success: false,
        projectId,
        error: `Failed to create project: ${errorMessage}`,
      };
    }
  }

  /**
   * Upload file to project staging folder using signed URL
   * This uses a 3-step process to avoid Cloud Run's 32MB request limit:
   * 1. Get signed URL from backend
   * 2. Upload file directly to Cloud Storage
   * 3. Notify backend that upload is complete
   */
  async uploadFileToProject(projectId: string, file: File): Promise<ProjectCreationResult> {
    try {
      ClientLoggingService.log(
        'INFO',
        `[ProjectCreationService] Starting signed URL upload for file: ${file.name} (${file.size} bytes)`
      );

      // Step 2a: Generate signed URL
      ClientLoggingService.log('INFO', `[ProjectCreationService] Step 2a: Requesting signed URL...`);
      const urlRequest = new GenerateUploadUrlRequest();
      urlRequest.setProjectId(projectId);
      urlRequest.setFilename(file.name);
      urlRequest.setContentType(file.type);
      urlRequest.setFileSize(file.size);

      const urlResponse = await this.writeClient.generateUploadUrl(urlRequest, null);
      const urlResponseObj = urlResponse.toObject();

      if (!urlResponseObj.success || !urlResponseObj.uploadUrl) {
        throw new Error(`Failed to generate upload URL: ${urlResponseObj.message || 'Unknown error'}`);
      }

      const canonicalFilename = urlResponseObj.filename || file.name;
      const replacedExistingFile = !!urlResponseObj.replacesExistingFile;

      ClientLoggingService.log('INFO', `[ProjectCreationService] Step 2a completed: Received signed URL`);

      // Step 2b: Upload file directly to Cloud Storage using XMLHttpRequest for progress tracking
      ClientLoggingService.log('INFO', `[ProjectCreationService] Step 2b: Uploading file directly to Cloud Storage...`);
      await this.uploadWithProgress(urlResponseObj.uploadUrl, file);

      ClientLoggingService.log('INFO', `[ProjectCreationService] Step 2b completed: File uploaded to Cloud Storage`);

      // Step 2c: Notify backend that upload is complete
      ClientLoggingService.log('INFO', `[ProjectCreationService] Step 2c: Notifying backend of upload completion...`);
      const notifyRequest = new NotifyUploadCompleteRequest();
      notifyRequest.setProjectId(projectId);
      notifyRequest.setFilename(canonicalFilename);
      notifyRequest.setFileSize(file.size);

      const notifyResponse = await this.writeClient.notifyUploadComplete(notifyRequest, null);
      const notifyResponseObj = notifyResponse.toObject();

      if (!notifyResponseObj.success) {
        if (notifyResponseObj.uploadVerified) {
          return {
            success: false,
            projectId,
            canonicalFilename,
            replacedExistingFile,
            uploadVerified: true,
            metadataGenerated: false,
            partialUpload: true,
            fileId: notifyResponseObj.fileId || undefined,
            error:
              notifyResponseObj.metadataError ||
              'The file was uploaded, but metadata registration failed. Retry metadata from Project Files.',
          };
        }
        throw new Error(`Upload verification failed: ${notifyResponseObj.message || 'Unknown error'}`);
      }

      ClientLoggingService.log(
        'INFO',
        `[ProjectCreationService] Step 2c completed: Backend notified of successful upload (page_count: ${notifyResponseObj.pageCount})`
      );
      ClientLoggingService.log('INFO', `[ProjectCreationService] File uploaded successfully via signed URL:`, {
        projectId,
        filename: canonicalFilename,
        fileSize: file.size,
        filePath: notifyResponseObj.filePath,
        pageCount: notifyResponseObj.pageCount,
      });

      return {
        success: true,
        projectId,
        pageCount: notifyResponseObj.pageCount || undefined,
        canonicalFilename,
        replacedExistingFile,
        uploadVerified: !!notifyResponseObj.uploadVerified,
        metadataGenerated: !!notifyResponseObj.metadataGenerated,
        fileId: notifyResponseObj.fileId || undefined,
        message: replacedExistingFile
          ? `Updated existing file "${canonicalFilename}" in place.`
          : canonicalFilename !== file.name
            ? `File uploaded as "${canonicalFilename}".`
            : 'File uploaded successfully via Cloud Storage',
      };
    } catch (error) {
      ClientLoggingService.log('ERROR', '[ProjectCreationService] Error uploading file via signed URL:', error);

      // Provide more specific error information
      let errorMessage = 'Failed to upload file';
      if (error instanceof Error) {
        errorMessage += `: ${error.message}`;

        // Check for specific gRPC error codes
        if (error.message.includes('PERMISSION_DENIED')) {
          errorMessage = 'Permission denied - you may not have access to this project';
        } else if (error.message.includes('413') || error.message.includes('Payload Too Large')) {
          errorMessage = 'File size limit exceeded. This should not happen with signed URLs - please contact support.';
        } else if (error.message.includes('DEADLINE_EXCEEDED') || error.message.includes('timeout')) {
          errorMessage = 'Upload timed out. Please try again.';
        } else if (error.message.includes('NOT_FOUND')) {
          errorMessage = 'Project not found. It may have been deleted.';
        } else if (error.message.includes('UNAUTHENTICATED')) {
          errorMessage = 'Authentication failed. Please sign in again.';
        } else if (error.message.includes('Cloud Storage upload failed')) {
          errorMessage = 'Direct upload to Cloud Storage failed. Please try again.';
        }
      }

      return {
        success: false,
        projectId,
        error: errorMessage,
      };
    }
  }

  /**
   * Helper: Upload file using XMLHttpRequest with progress tracking
   * Uses ProgressService to show upload progress in the global progress bar
   */
  private uploadWithProgress(uploadUrl: string, file: File): Promise<void> {
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();

      // Track upload progress
      xhr.upload.addEventListener('progress', event => {
        if (event.lengthComputable) {
          const progress = Math.round((event.loaded / event.total) * 100);
          const loadedMB = (event.loaded / 1024 / 1024).toFixed(1);
          const totalMB = (event.total / 1024 / 1024).toFixed(1);
          this.progressService.updateProgress(`Uploading ${file.name}... ${loadedMB} MB / ${totalMB} MB`, progress);
        }
      });

      // Handle completion
      xhr.addEventListener('load', () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          resolve();
        } else {
          reject(new Error(`Cloud Storage upload failed (${xhr.status}): ${xhr.responseText || 'Unknown error'}`));
        }
      });

      // Handle errors
      xhr.addEventListener('error', () => {
        reject(new Error('Cloud Storage upload failed: Network error'));
      });

      xhr.addEventListener('abort', () => {
        reject(new Error('Cloud Storage upload was aborted'));
      });

      // Open and send
      xhr.open('PUT', uploadUrl);
      xhr.setRequestHeader('Content-Type', file.type || 'application/octet-stream');
      xhr.send(file);
    });
  }

  /**
   * Ingest file into project using async tasks (with Cloud Run Jobs support).
   * Creates individual tasks for each page to enable parallel processing.
   *
   * @param totalPageCount When set, creates tasks for pages [1..totalPageCount] ("all pages" mode).
   * @param customPages    Comma-separated page list / ranges (e.g. "1-3,5").
   *                       At most one of totalPageCount or customPages should be provided.
   */
  async ingestFileIntoProjectAsync(
    projectId: string,
    fileName: string,
    totalPageCount?: number,
    customPages?: string
  ): Promise<ProjectCreationResult> {
    try {
      if (totalPageCount && customPages) {
        return {
          success: false,
          projectId,
          error: 'Cannot specify both totalPageCount and customPages — provide one or neither.',
        };
      }

      ClientLoggingService.log(
        'INFO',
        `[ProjectCreationService] Starting async ingestion for file: ${fileName}` +
          (customPages
            ? ` (pages: ${customPages})`
            : totalPageCount
              ? ` (all ${totalPageCount} pages)`
              : ' (all pages, count unknown)')
      );

      let pageNumbers: number[] = [];
      if (customPages) {
        pageNumbers = this.parsePageNumbers(customPages);
        if (pageNumbers.length === 0) {
          throw new Error('Invalid page numbers specified');
        }
      } else if (totalPageCount && totalPageCount > 0) {
        pageNumbers = Array.from({ length: totalPageCount }, (_, i) => i + 1);
      } else {
        ClientLoggingService.log(
          'WARN',
          `[ProjectCreationService] Page count not available — falling back to page 1 only`
        );
        pageNumbers = [1];
      }

      ClientLoggingService.log('INFO', `[ProjectCreationService] Processing pages: ${pageNumbers.join(', ')}`);

      const billingOperationId = createBillingOperationId();
      let billingPreflight: BalanceCheckResult;
      try {
        billingPreflight = await this.billingService.checkSufficientBalance(
          0,
          'plan-ingestion',
          pageNumbers.length,
          billingOperationId
        );
      } catch (error) {
        ClientLoggingService.log(
          'ERROR',
          `[ProjectCreationService] Billing preflight unavailable for operation ${billingOperationId}`,
          error
        );
        return {
          success: false,
          projectId,
          error: BILLING_VALIDATION_UNAVAILABLE_MESSAGE,
        };
      }
      if (billingPreflight.billingUnavailable) {
        return {
          success: false,
          projectId,
          error: BILLING_VALIDATION_UNAVAILABLE_MESSAGE,
        };
      }
      if (!billingPreflight.hasSufficientBalance) {
        return {
          success: false,
          projectId,
          error: 'Insufficient available balance. No ingestion was started.',
        };
      }

      // Create async tasks for each page. Dispatch is concurrency-limited (Issue #1593):
      // firing every page's RPC via a single Promise.all overwhelms the browser's own
      // network stack on large files (net::ERR_INSUFFICIENT_RESOURCES), and a plain
      // Promise.all would also discard every already-succeeded task the moment any single
      // page failed. runWithConcurrencyLimit bounds in-flight requests and lets each page
      // settle independently.
      const { results, failures, skipped } = await runWithConcurrencyLimit(
        pageNumbers,
        BULK_INGESTION_DISPATCH_CONCURRENCY,
        async pageNumber => {
          const response = await lastValueFrom(
            this.taskService.startAsyncIngestFile(
              projectId,
              fileName,
              pageNumber,
              false, // forceReprocess
              true, // enableOrientationDetection
              undefined,
              {
                operationId: billingOperationId,
                taskCount: pageNumbers.length,
                childKey: String(pageNumber),
              }
            )
          );

          const responseObj = response.toObject();
          if (!responseObj.success || !responseObj.taskId) {
            throw new Error(responseObj.message || 'Failed to create async task');
          }
          ClientLoggingService.log(
            'INFO',
            `[ProjectCreationService] Created async task ${responseObj.taskId} for page ${pageNumber}`
          );
          return responseObj.taskId;
        },
        { stopOnFailure: isOperationWideBillingFailure }
      );

      const operationWideFailure = failures.find(failure => isOperationWideBillingFailure(failure.error));
      if (operationWideFailure) {
        ClientLoggingService.log(
          'ERROR',
          `[ProjectCreationService] Billing integrity stopped operation ${billingOperationId}: ` +
            `${results.filter(result => result).length} started, ${skipped} not dispatched`,
          operationWideFailure.error
        );
      } else {
        failures.forEach(failure =>
          ClientLoggingService.log(
            'ERROR',
            `[ProjectCreationService] Failed to create task for page ${failure.item}:`,
            failure.error
          )
        );
      }

      const taskIds = results.filter((taskId): taskId is string => !!taskId);

      ClientLoggingService.log(
        'INFO',
        `[ProjectCreationService] Created ${taskIds.length} of ${pageNumbers.length} async ingestion tasks` +
          (failures.length > 0 ? ` (${failures.length} failed, ${skipped} not dispatched)` : '')
      );

      if (taskIds.length === 0 && pageNumbers.length > 0) {
        return {
          success: false,
          projectId,
          error: operationWideFailure
            ? BILLING_VALIDATION_UNAVAILABLE_MESSAGE
            : `Failed to create any of the ${pageNumbers.length} requested ingestion tasks.`,
        };
      }

      const message = operationWideFailure
        ? `Started async processing for ${taskIds.length} page(s); billing validation stopped the remaining ${skipped + failures.length} page(s).`
        : failures.length > 0
          ? `Started async processing for ${taskIds.length} of ${pageNumbers.length} page(s); ${failures.length} failed to start and can be retried from Project Settings.`
          : `Started async processing for ${taskIds.length} page(s). Tasks will run in the background using Cloud Run Jobs.`;

      return {
        success: true,
        projectId,
        taskIds,
        message,
      };
    } catch (error) {
      ClientLoggingService.log('ERROR', '[ProjectCreationService] Error starting async file ingestion:', error);
      return {
        success: false,
        projectId,
        error: `Failed to start async processing: ${error instanceof Error ? error.message : 'Unknown error'}`,
      };
    }
  }

  /**
   * Legacy synchronous ingest method (kept for backward compatibility)
   * @deprecated Use ingestFileIntoProjectAsync for better scalability
   */
  async ingestFileIntoProject(
    projectId: string,
    fileName: string,
    customPages?: string
  ): Promise<ProjectCreationResult> {
    try {
      // Create the request
      const request = new IngestFileIntoProjectRequest();
      request.setProjectId(projectId);
      request.setFilename(fileName);

      // Add custom pages if specified
      if (customPages) {
        const pageNumbers = customPages
          .split(',')
          .map(p => parseInt(p.trim(), 10))
          .filter(p => !isNaN(p));
        request.setPageNumbersList(pageNumbers);
      }

      ClientLoggingService.log(
        'INFO',
        `[ProjectCreationService] Ingesting file: ${fileName}${customPages ? ` (pages: ${customPages})` : ' (all pages)'}`
      );

      // Make the gRPC call
      const response = await this.writeClient.ingestFileIntoProject(request, null);

      ClientLoggingService.log('INFO', `[ProjectCreationService] File ingested successfully:`, response.toObject());

      return {
        success: true,
        projectId,
        message: 'File processed and content extracted successfully',
      };
    } catch (error) {
      ClientLoggingService.log('ERROR', '[ProjectCreationService] Error ingesting file:', error);
      return {
        success: false,
        projectId,
        error: `Failed to process file: ${error instanceof Error ? error.message : 'Unknown error'}`,
      };
    }
  }

  /**
   * Get project metadata from the project-metadata.json file
   */
  async getProjectMetadata(
    projectId: string
  ): Promise<{ success: boolean; metadata?: ProjectMetadata; error?: string }> {
    try {
      ClientLoggingService.log('INFO', `[ProjectCreationService] Getting project metadata for: ${projectId}`);

      // Create the request
      const request = new GetProjectMetadataRequest();
      request.setArchitecturalProjectId(projectId);

      // Make the gRPC call
      const response = await this.client.getProjectMetadata(request, null);
      const responseObj = response.toObject();

      // Use new metadata field (Issue #227) or fall back to legacy fields
      const metadataObj = responseObj.metadata;
      const metadata: ProjectMetadata = {
        project_id: metadataObj?.projectId || responseObj.architecturalProjectId || projectId,
        project_name: metadataObj?.projectName || responseObj.architecturalProjectName || `Project ${projectId}`,
        description: metadataObj?.projectDescription || responseObj.description || '',
        created_by: metadataObj?.createdBy || responseObj.createdBy || '',
        created_at: metadataObj?.createdAt || responseObj.createdAt || '',
        status: metadataObj?.status || responseObj.status || 'unknown',
        // Include address from new schema
        address: metadataObj?.projectAddress
          ? {
              street: metadataObj.projectAddress.street || '',
              city: metadataObj.projectAddress.city || '',
              state: metadataObj.projectAddress.state || '',
              postalCode: metadataObj.projectAddress.postalCode || '',
              country: metadataObj.projectAddress.country || '',
              // Google Maps metadata (Issue #236)
              googlePlaceId: metadataObj.projectAddress.googlePlaceId || undefined,
              latitude: metadataObj.projectAddress.latitude || undefined,
              longitude: metadataObj.projectAddress.longitude || undefined,
              formattedAddress: metadataObj.projectAddress.formattedAddress || undefined,
            }
          : undefined,
        modelConfig: metadataObj?.modelConfig
          ? {
              useCaseOverrides: (metadataObj.modelConfig.useCaseOverridesList || []).map(
                (override: { useCase: number; profileId: string; roleProfilesMap: Array<[string, string]> }) => ({
                  useCase: this.useCaseEnumToString(override.useCase),
                  profileId: override.profileId || '',
                  roleProfiles: Object.fromEntries(override.roleProfilesMap || []),
                })
              ),
            }
          : undefined,
        ingestionSettings: metadataObj?.ingestionSettings
          ? {
              enableVisualVerification: metadataObj.ingestionSettings.enableVisualVerification ?? false,
              enableRegionHierarchy: metadataObj.ingestionSettings.enableRegionHierarchy ?? false,
            }
          : undefined,
        power_user_mode: metadataObj?.powerUserMode ?? false,
      };

      ClientLoggingService.log('INFO', `[ProjectCreationService] Project metadata loaded successfully:`, metadata);

      return {
        success: true,
        metadata,
      };
    } catch (error) {
      ClientLoggingService.log('ERROR', '[ProjectCreationService] Error getting project metadata:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error occurred',
      };
    }
  }

  /**
   * List uploaded files in the project's inputs folder
   */
  async getUploadedFiles(projectId: string): Promise<{ success: boolean; files?: UploadedFile[]; error?: string }> {
    try {
      ClientLoggingService.log('INFO', `[ProjectCreationService] Getting uploaded files for: ${projectId}`);

      // Create the request
      const request = new ListArchitecturalProjectInputFilesRequest();
      request.setArchitecturalProjectId(projectId);

      // Make the gRPC call
      const response = await this.client.listArchitecturalProjectInputFiles(request, null);
      const responseObj = response.toObject();

      // Convert file paths to UploadedFile objects
      // Note: file_paths from backend include "inputs/" prefix (e.g., "inputs/filename.pdf")
      // We strip this prefix for display while keeping the full path for backend operations
      const files: UploadedFile[] =
        responseObj.filePathsList?.map((filePath: string) => {
          // Extract just the filename by removing the "inputs/" prefix
          const fileName = filePath.startsWith('inputs/')
            ? filePath.substring(7) // Remove "inputs/" (7 characters)
            : filePath.split('/').pop() || filePath; // Fallback for other path formats

          return {
            name: fileName,
            size: 0, // Size not available from this endpoint
            uploadedAt: new Date().toISOString(), // Upload time not available
            path: filePath, // Keep full path for backend operations
            pageCount: undefined, // Page count not available from this endpoint
            fileType: fileName.toLowerCase().endsWith('.pdf') ? 'PDF' : 'Unknown',
            lastModified: undefined, // Last modified not available from this endpoint
          };
        }) || [];

      ClientLoggingService.log(
        'INFO',
        `[ProjectCreationService] Raw file paths from backend:`,
        responseObj.filePathsList
      );
      ClientLoggingService.log(
        'INFO',
        `[ProjectCreationService] Processed files for display:`,
        files.map(f => ({ name: f.name, path: f.path }))
      );

      return {
        success: true,
        files,
      };
    } catch (error) {
      ClientLoggingService.log('ERROR', '[ProjectCreationService] Error getting uploaded files:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error occurred',
      };
    }
  }

  /**
   * Parse page numbers string that supports ranges (e.g., "1,2,3-5,7")
   */
  private parsePageNumbers(pagesStr: string): number[] {
    const pages: number[] = [];
    const parts = pagesStr.split(',');

    for (const part of parts) {
      const trimmed = part.trim();
      if (trimmed.includes('-')) {
        // Handle range like "3-5"
        const [start, end] = trimmed.split('-').map(s => parseInt(s.trim(), 10));
        if (!isNaN(start) && !isNaN(end) && start <= end) {
          for (let i = start; i <= end; i++) {
            if (!pages.includes(i)) {
              pages.push(i);
            }
          }
        }
      } else {
        // Handle individual page number
        const pageNum = parseInt(trimmed, 10);
        if (!isNaN(pageNum) && !pages.includes(pageNum)) {
          pages.push(pageNum);
        }
      }
    }

    return pages.sort((a, b) => a - b); // Sort pages in ascending order
  }

  private static readonly USE_CASE_NAMES: Record<number, string> = {
    0: 'LLM_USE_CASE_UNSPECIFIED',
    1: 'PLAN_INGESTION',
    2: 'CODE_APPLICABILITY',
    3: 'COMPLIANCE_ANALYSIS',
    4: 'CHAT_AGENT',
    5: 'CHAT_DECORATOR',
    6: 'CHAT_SESSION_SUMMARY',
    7: 'VOICE_TRANSCRIPT_CLEANUP',
    8: 'IMAGE_OBJECT_SEARCH',
    9: 'IMAGE_SEARCH_ENSEMBLE',
    10: 'IMAGE_SEARCH_DIRECT',
    11: 'IMAGE_SEARCH_AGENT',
    50: 'ICC_CHAPTER_SUMMARY',
  };

  private useCaseEnumToString(enumValue: number): string {
    return ProjectCreationService.USE_CASE_NAMES[enumValue] || `UNKNOWN_${enumValue}`;
  }
}
